import { useState, useEffect, useMemo, useRef } from 'react';
import axios from "axios";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter } from "../../services/loanApi";
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from "../../utils/loanEditLock";
import Loading from "../layout/Loading";
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const CreditReportForm = () => {
    const [borrowerLegacyId, setBorrowerLegacyId] = useState('');
    const triggerSubmitRef = useRef(false);
    // Form fields state
    const [loanID, setLoanID] = useState('');
    const [selectedBorrower, setSelectedBorrower] = useState('');
    const [reportType, setReportType] = useState('Merge');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [requestType, setRequestType] = useState('NewOrder');
    const [reportOn, setReportOn] = useState('Individual');
    const [borrowerDropdownOptions, setBorrowerDropdownOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialSubmitDate, setInitialSubmitDate] = useState('');
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState('success'); // 'success' | 'error' | 'warning'
    const [toastMessage, setToastMessage] = useState('');
    const [toastAnim, setToastAnim] = useState('animate-fade-in'); // optional css animation class
    const [isLoading, setIsLoading] = useState(false);
    const [showRequestTypeModal, setShowRequestTypeModal] = useState(false);
    const [pendingRequestType, setPendingRequestType] = useState('');

    // per-application & authorization state
    const borrowerPairNo = useMemo(() => {
        const idx = parseInt(selectedBorrower || '0', 10);
        return Number.isFinite(idx) ? idx + 1 : 1;
    }, [selectedBorrower]);

    const [selectedHasCoborrower, setSelectedHasCoborrower] = useState(false);

    // Borrower auth (4073#N / 4074#N)
    const [authBorrChecked, setAuthBorrChecked] = useState(false);
    const [authBorrDate, setAuthBorrDate] = useState('');

    // Co-borrower auth (4076#N / 4077#N)
    const [authCoChecked, setAuthCoChecked] = useState(false);
    const [authCoDate, setAuthCoDate] = useState('');

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async (loanObject) => {
                loanObject.all().then(async (loan) => {
                    info("TPO Header loaded", {
                        loanid: loan.id,
                        loanNumber: loan.loanNumber || "N/A"
                    });
                    setLoanID(loan.id);

                    const applications = loan.applications || [];

                    const tpo = loan?.tpo || {};
                    const submittedDate = tpo.initialSubmitDate || '';
                    setInitialSubmitDate(submittedDate);

                    // Build dropdown options per application
                    const borrowerOptions = applications.map((app, index) => {
                        const borrowerName = app?.borrower?.fullName || '';
                        const borrowerLegacyId = app?.borrower?.legacyId || '';
                        const hasCo = !!app?.coborrower?.fullName;

                        let displayName = borrowerName || `Application ${index + 1}`;
                        if (hasCo) {
                            displayName = `${borrowerName} & ${app.coborrower.fullName}`;
                        }

                        return {
                            label: displayName,
                            value: index.toString(), // store index
                            legacyId: borrowerLegacyId,
                            borrowerName: displayName,
                            refNumber: app?.creditReportReferenceIdentifier || '',
                            hasCoborrower: hasCo
                        };
                    });

                    setBorrowerDropdownOptions(borrowerOptions);

                    if (borrowerOptions.length > 0) {
                        // Default to first application
                        const first = borrowerOptions[0];
                        setSelectedBorrower(first.value);
                        setBorrowerLegacyId(first.legacyId);
                        setReferenceNumber(first.refNumber || '');
                        setSelectedHasCoborrower(first.hasCoborrower);

                        setRequestType(first.refNumber ? 'Reissue' : 'NewOrder');
                    }
                });
            });
        } catch (error) {
            const errorCode = logError("CREDIT_REPORT_ERROR");
            logAppInsightError("credit report Error", {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load credit report information.`,
                component: "creditreport"
            });
        }
    };
    const buildWriterPayload = (readerValues) => {
        return Object.entries(readerValues).map(([id, val]) => {
            if (val === "Y" || val === "N") {
                return { id, value: val === "Y" ? "true" : "false" };
            }
            return { id, value: val }; // dates passthrough
        });
    };

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);
    useEffect(() => {
        if (!loanID) return;

        (async () => {
            try {
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const authCode = await tpoAuthObject.createAuthCode();
                const token = await getAccessToken(authCode);
                const values = await fieldReader(token, LOAN_EDIT_LOCK_FIELD_IDS, loanID);
                setEditLocked(isLoanEditLocked(values));
            } catch (err) {
                console.error("Failed to read loan edit-lock fields", err);
            }
        })();
    }, [loanID]);

    useEffect(() => {
        if (triggerSubmitRef.current) {
            triggerSubmitRef.current = false;
            handleSubmit();
        }
    }, [requestType, referenceNumber]);

    // Load authorization fields when selection changes
    const loadAuthorization = async (appHasCo) => {
        try {
            setIsLoading(true);
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCodes = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCodes);

            const coBorrowerFieldId = `4006#${borrowerPairNo}`;

            const ids = [`4073#${borrowerPairNo}`, `4074#${borrowerPairNo}`, coBorrowerFieldId];

            if (appHasCo) {
                ids.push(`4076#${borrowerPairNo}`, `4077#${borrowerPairNo}`);
            }

            const values = await fieldReader(token, ids, loanID);

            const v4073 = (values[`4073#${borrowerPairNo}`] || '').toString().trim().toUpperCase();
            const v4074 = values[`4074#${borrowerPairNo}`] || '';
            const v4076 = (values[`4076#${borrowerPairNo}`] || '').toString().trim().toUpperCase();
            const v4077 = values[`4077#${borrowerPairNo}`] || '';
            const coBorrowerLastName = (values[coBorrowerFieldId] || "").trim();

            setAuthBorrChecked(v4073 === 'Y');
            setAuthBorrDate(v4074 ? toMMDDYYYY(v4074) : '');

            if (appHasCo) {
                setAuthCoChecked(v4076 === 'Y');
                setAuthCoDate(v4077 ? toMMDDYYYY(v4077) : '');
            } else {
                setAuthCoChecked(false);
                setAuthCoDate('');
            }

            // Auto set Report On to Joint
            if (coBorrowerLastName) {
                setReportOn('Joint');
            } else {
                setReportOn('Individual');
            }
        } catch (err) {
            logAppInsightError("loadAuthorization error", { err, component: "CreditReportForm" });
        } finally {
            setIsLoading(false);
        }
    };

    // Reload auth when the selected application changes
    useEffect(() => {
        if (!borrowerDropdownOptions.length) return;
        const current = borrowerDropdownOptions.find(o => o.value === selectedBorrower);
        if (!current) return;

        setSelectedHasCoborrower(current.hasCoborrower);
        setBorrowerLegacyId(current.legacyId || '');
        setReferenceNumber(current.refNumber || '');
        setRequestType(current.refNumber ? 'Reissue' : 'NewOrder');

        loadAuthorization(current.hasCoborrower);
    }, [selectedBorrower, borrowerDropdownOptions, borrowerPairNo]);

    const toMMDDYYYY = (d) => {
        if (!d) return "";
        // already mm/dd/yyyy?
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return "";
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        const yyyy = dt.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
    };

    const authorizationValid = (() => {
        const borrowerOK = authBorrChecked && !!authBorrDate;
        return selectedHasCoborrower ? (borrowerOK && authCoChecked && !!authCoDate) : borrowerOK;
    })();

    const saveAuthorization = async () => {
        const base = {
            [`4073#${borrowerPairNo}`]: authBorrChecked ? "Y" : "N",
            [`4074#${borrowerPairNo}`]: toMMDDYYYY(authBorrDate) || toMMDDYYYY(new Date())
        };
        if (selectedHasCoborrower) {
            base[`4076#${borrowerPairNo}`] = authCoChecked ? "Y" : "N";
            base[`4077#${borrowerPairNo}`] = toMMDDYYYY(authCoDate) || toMMDDYYYY(new Date());
        }

        const payload = buildWriterPayload(base);

        const tpoAuthObject = await window.elli.script.getObject("auth");
        const ac = await tpoAuthObject.createAuthCode();
        const token = await getAccessToken(ac);

        // fieldWriter expects 204 on success
        await fieldWriter(token, loanID, payload);
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        const hasReference = referenceNumber?.trim()?.length > 0;
        try {
            if (!authorizationValid) {
                showToast('warning', 'Please authorize credit (checkbox + date) for the required applicant(s) before ordering.');
                return;
            }

            if (requestType === 'Reissue' && !hasReference) {
                showToast('warning', 'Reference number is required for reissue.');
                return;
            }

            if (requestType === 'NewOrder' && hasReference) {
                setShowRequestTypeModal(true);
                return;
            }
            setIsSubmitting(true);

            // Save auth fields first
            await saveAuthorization();

            const url = `${API_DOMAIN}/api/credit-order/place`;
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const fromBody = {
                authCode,
                entityRef: {
                    loanId: loanID,
                    borrowerId: borrowerLegacyId
                },
                options: {
                    requestType,
                    reportOn,
                    reportType,
                    creditReportIdentifier: referenceNumber !== "" ? referenceNumber : ""
                }
            };

            const response = axios.post(url, fromBody, {
                headers: { "Content-Type": "application/json" }
            }).then(response => {
                return response;
            })
                .catch(error => {
                    console.error('Unexpected status:', error.response ? error.response.data : error.message);
                    throw new Error(`Unexpected status: ${response.status} ${response.statusText}`);
                });

            // Fetch txnId from CX.CREDIT.TXN.IDS
            const authCodes2 = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCodes2);
            const fieldsToFetch = ["CX.CREDIT.TXN.IDS"];
            const filedResponse = await fieldReader(token, fieldsToFetch, loanID);
            const fullID = filedResponse["CX.CREDIT.TXN.IDS"];
            const txnId = getTransactionId(fullID, borrowerLegacyId);

            const statusAuthCode = await tpoAuthObject.createAuthCode();
            const finalStatus = await pollOrderStatus(txnId, statusAuthCode);

            if (finalStatus === "success") {
                initializeAuthObject();
                showToast('success', 'Credit order placed and confirmed.');
            } else {
                showToast('warning', 'Credit order placed, but transaction is not confirmed.');
            }
        } catch (error) {
            const errCode = logError("CREDIT_ORDER_ERROR");
            showToast('error', `Credit order failed.<br>${error.message || error}`);
            logAppInsightError("handleSubmit error", {
                problemId: errCode,
                rawError: error,
                component: "CreditReportForm"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const getTransactionId = (trans, legacyId) => {
        if (!trans) return null;
        const segments = trans.split('|');
        for (const segment of segments) {
            const [id, txn] = segment.split('~');
            if (id === legacyId) return txn;
        }
        return null;
    };

    const handleViewPdf = async () => {
        try {
            setLoading(true);
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            const fieldsToFetch = ["CX.CREDIT.TXN.IDS"];
            const filedResponse = await fieldReader(token, fieldsToFetch, loanID);
            const fullID = filedResponse["CX.CREDIT.TXN.IDS"];
            const txnId = getTransactionId(fullID, borrowerLegacyId);

            const authCodes = await tpoAuthObject.createAuthCode();
            const url = `${API_DOMAIN}/api/credit-order/download/${txnId}?authCode=${authCodes}`;
            const response = axios.get(url, {
                responseType: 'blob',
                headers: { "Content-Type": "application/json" },
            }).then(response => {
                return response;
            })
                .catch(error => {
                    console.error('Unexpected status:', error.response ? error.response.data : error.message);
                    setLoading(false);
                    throw new Error(error.message || 'Failed to fetch PDF.');
                });

            const blob = response.data;
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (error) {
            logAppInsightError("handleViewPdf error", { error, component: "CreditReportForm" });
            showToast('error', 'Failed to open credit PDF.');
        } finally {
            setLoading(false);
        }
    };

    const pollOrderStatus = async (txnId, authCode) => {
        const url = `${API_DOMAIN}/api/credit-order/status/${txnId}?authCode=${authCode}`;
        try {
            const response = await axios.get(url);
            const status = response.data?.status;
            if (status === "success") return status;
        } catch (error) {
            showToast('error', 'Checking credit transaction status failure');
        }
        throw new Error("Status polling timed out or failed.");
    };

    const showToast = (type, htmlMessage) => {
        setToastType(type);
        setToastMessage(htmlMessage);
        setToastAnim('animate-fade-in');
        setToastVisible(true);
    };

    const closeToast = () => {
        setToastVisible(false);
    };

    useEffect(() => {
        if (toastVisible) {
            const timer = setTimeout(() => {
                setToastVisible(false);
            }, 10000); // 10 seconds

            return () => clearTimeout(timer);
        }
    }, [toastVisible]);

    if (loading) return <Loading />;

    return (
        <>
            <Toast
                visible={toastVisible}
                type={toastType}
                message={toastMessage}
                onClose={closeToast}
                animationClass={toastAnim}
            />

            <LoadingOverlay loading={isLoading} text="Loading..." />
            {isSubmitting && (
                <LoadingOverlay loading={true} text="Submitting..." />
            )}

            {showRequestTypeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-lg w-[400px] p-6">
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">
                            Existing Credit Reference Found
                        </h2>
                        <p className="text-sm text-gray-600 mb-6">
                            A reference number already exists for this borrower. <br />
                            Do you want to pull a <strong>new credit report</strong> or <strong>reissue</strong> using the existing reference?
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                                onClick={() => {
                                    setShowRequestTypeModal(false);
                                    setRequestType('Reissue');
                                    showToast('info', 'Changed to Reissue based on your selection, proceeding to submit');
                                    triggerSubmitRef.current = true;
                                }}
                            >
                                Reissue
                            </button>

                            <button
                                className="px-4 py-2 rounded bg-[#2080C3] hover:bg-[#5DA5D9] text-white text-sm"
                                onClick={() => {
                                    setShowRequestTypeModal(false);
                                    setRequestType('NewOrder');
                                    setReferenceNumber('');
                                    showToast('info', 'Proceeding with a New Credit Order.');
                                    triggerSubmitRef.current = true;
                                }}
                            >
                                Pull New Credit
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <div className="flex items-center justify-between border-b border-[#8c93a6] mb-4">
                        <div className="page-title1">Corelogic Credco</div>

                        {referenceNumber && (
                            <a
                                href="#"
                                onClick={handleViewPdf}
                                className="text-sky-700 hover:text-sky-900 text-sm font-semibold underline uppercase"
                            >
                                View Credit Report
                            </a>
                        )}
                    </div>


                    <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                        <div className="section-header">Order/ Reissue Credit</div>
                        <div className="px-4 py-4">
                            <form
                                {...((!initialSubmitDate || initialSubmitDate === '//') && !editLocked ? { onSubmit: handleSubmit } : {})}
                            >
                                <div className="rounded-md p-4 mb-3 pd-0px">
                                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                        {/* Borrower selection */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="form-label mb-1">
                                                    Borrowers<span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    className="form-input w-full text-sm border rounded p-1"
                                                    value={selectedBorrower}
                                                    onChange={(e) => {
                                                        const selected = borrowerDropdownOptions.find(opt => opt.value === e.target.value);
                                                        if (selected) {
                                                            setSelectedBorrower(selected.value);
                                                            setBorrowerLegacyId(selected.legacyId);
                                                            setReferenceNumber(selected.refNumber || '');
                                                            setSelectedHasCoborrower(!!selected.hasCoborrower);
                                                            setReportOn('Individual');

                                                            if (selected.refNumber) {
                                                                setRequestType('Reissue');
                                                            } else {
                                                                setRequestType('NewOrder');
                                                                setReferenceNumber('');
                                                            }
                                                        }
                                                    }}
                                                    required
                                                >
                                                    {borrowerDropdownOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div></div>
                                        </div>

                                        {/* Report Type & Reference Number */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="form-label mb-1">Report Type<span className="text-red-500">*</span></label>
                                                <select
                                                    className="form-input w-full text-sm border rounded p-1"
                                                    value={reportType}
                                                    onChange={(e) => setReportType(e.target.value)}
                                                    required
                                                >
                                                    <option value="Merge">Merge</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">Reference Number</label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    value={referenceNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setReferenceNumber(value);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Request Type & Report On */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                            <div>
                                                <label className="form-label mb-1">Request Type</label>
                                                <select
                                                    className="form-input w-full text-sm border rounded p-1"
                                                    value={requestType}
                                                    onChange={(e) => {
                                                        const selectedType = e.target.value;
                                                        const hasReference = referenceNumber?.trim()?.length > 0;
                                                        // Normal flow
                                                        setRequestType(selectedType);
                                                        // Save pending selection to apply after modal confirmation
                                                        setPendingRequestType(selectedType);

                                                        if (selectedType === 'Reissue' && !hasReference) {
                                                            showToast('warning', 'Reference number is required for reissue.');
                                                            return;
                                                        }

                                                        if (selectedType === 'NewOrder' && hasReference) {
                                                            setShowRequestTypeModal(true);
                                                            return;
                                                        }

                                                        if (selectedType === 'Reissue' && hasReference) {
                                                            initializeAuthObject();
                                                        }
                                                    }}
                                                >
                                                    <option value="NewOrder">NewOrder</option>
                                                    <option value="Reissue">Reissue</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">Report On</label>
                                                <select
                                                    className="form-input w-full text-sm border rounded p-1"
                                                    value={reportOn}
                                                    onChange={(e) => setReportOn(e.target.value)}
                                                >
                                                    <option value="Individual">Individual</option>
                                                    <option value="Joint">Joint</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="mb-3 text-sm font-semibold text-slate-800">Authorization</div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                                {/* Borrower */}
                                                <div className="space-y-2">
                                                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                                        <input
                                                            type="checkbox"
                                                            className="h-3.5 w-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                                            checked={authBorrChecked}
                                                            onChange={(e) => {
                                                                const c = e.target.checked;
                                                                setAuthBorrChecked(c);
                                                                if (c && !authBorrDate) setAuthBorrDate(new Date().toISOString().slice(0, 10));
                                                            }}
                                                        />
                                                        <span className="font-medium">Borrower authorized credit</span>
                                                    </label>

                                                    <input
                                                        type="date"
                                                        className={`form-input w-28 min-w-0 text-xs px-1 py-0.5 leading-tight ${authBorrChecked && !authBorrDate ? 'ring-1 ring-rose-300 focus:ring-rose-400' : ''
                                                            }`}
                                                        value={authBorrDate ? new Date(authBorrDate).toISOString().slice(0, 10) : ''}
                                                        onChange={(e) => setAuthBorrDate(e.target.value)}
                                                    />
                                                </div>

                                                {/* Co-borrower */}
                                                {selectedHasCoborrower ? (
                                                    <div className="space-y-2">
                                                        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                                            <input
                                                                type="checkbox"
                                                                className="h-3.5 w-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                                                checked={authCoChecked}
                                                                onChange={(e) => {
                                                                    const c = e.target.checked;
                                                                    setAuthCoChecked(c);
                                                                    if (c && !authCoDate) setAuthCoDate(new Date().toISOString().slice(0, 10));
                                                                }}
                                                            />
                                                            <span className="font-medium">Co-Borrower authorized credit</span>
                                                        </label>

                                                        <input
                                                            type="date"
                                                            className={`form-input w-28 min-w-0 text-xs px-1 py-0.5 leading-tight ${authCoChecked && !authCoDate ? 'ring-1 ring-rose-300 focus:ring-rose-400' : ''
                                                                }`}
                                                            value={authCoDate ? new Date(authCoDate).toISOString().slice(0, 10) : ''}
                                                            onChange={(e) => setAuthCoDate(e.target.value)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="hidden md:block" />
                                                )}
                                            </div>

                                            {!authorizationValid && (
                                                <div className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-200">
                                                    Borrower authorization(s) is required to proceed.
                                                </div>
                                            )}
                                        </div>

                                    </div>

                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="submit"
                                            className={`btn-custom rounded transition-colors duration-200 ${isSubmitting ||
                                                (initialSubmitDate && initialSubmitDate !== '//') || editLocked ||
                                                !authorizationValid
                                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                                : 'text-[#FFFFFF] bg-[#2080C3] hover:text-[#E6EEF8] hover:bg-[#5DA5D9] active:bg-white'
                                                }`}
                                            disabled={
                                                isSubmitting ||
                                                (initialSubmitDate && initialSubmitDate !== '//') || editLocked ||
                                                !authorizationValid
                                            }
                                        >
                                            {isSubmitting
                                                ? "Submitting..."
                                                : referenceNumber
                                                    ? "Reissue Credit"
                                                    : "Submit"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default CreditReportForm;
